import React, { useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Modal from '@medflex/components/stories/Modal';
import { IconExclamationSolid } from '@medflex/components/stories/Icons';
import CreateSubContactForm from '@medflex/components/stories/CreateSubContactForm';
import { showSuccessToast } from '@medflex/components/stories/Toast';
import { isValidDateOfBirth } from '@medflex/shared-utils';
import useSubContacts from '../hooks/useSubContacts';
import useModal from '../hooks/useModal';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';

const PhysicianCreateSubcontactModalModule = () => {
  const { t } = useTranslation('physician');
  const { create } = useSubContacts();
  const [error, setError] = useState();
  const [submitted, setSubmitted] = useState(false);
  const { contactId, firstName, lastName, dateOfBirth, redirect, readonly } =
    useLocationSearchParams();
  const { close, getQueryString } = useModal();

  const schema = yup.object().shape({
    firstName: yup.string().trim().required(t('global:validation.messages.required')),
    lastName: yup.string().trim().required(t('global:validation.messages.required')),
    dateOfBirth: yup
      .string()
      .test('is proper date of birth', t('global:validation.messages.validAge'), value =>
        isValidDateOfBirth(new Date(value)),
      )
      .required(t('global:validation.messages.required')),
  });

  const handleSubmit = async values => {
    if (submitted) return;

    try {
      setSubmitted(true);
      await create({ ...values, contactId });
      const link = getQueryString(
        { modal: 'createSubcontact', contactId },
        { firstName, lastName, dateOfBirth },
      );

      showSuccessToast(
        t('PhysicianCreateSubContactModalModule.successToast.title'),
        <Link to={link} className="text-blue-500">
          {t('PhysicianCreateSubContactModalModule.successToast.text')}
        </Link>,
        { toastId: 'subContactSuccess' },
      );
      close({
        modal: 'createSubcontact',
        contactId,
        firstName,
        lastName,
        dateOfBirth,
        readonly,
        redirect,
      });
    } catch (e) {
      setSubmitted(false);
      setError(t('PhysicianCreateSubContactModalModule.unknownError'));
    }
  };

  return (
    <Modal
      showCloseButton
      title={t('PhysicianCreateSubContactModalModule.title')}
      onClickCloseButton={() =>
        close({
          modal: 'createSubcontact',
          contactId,
          firstName,
          lastName,
          dateOfBirth,
          readonly,
          redirect,
        })
      }
    >
      {error && (
        <p className="text-sm px-2 py-1 rounded border border-red-600 bg-red-200 text-red-600 mb-4 flex">
          <IconExclamationSolid size="text" className="mt-1" />
          <span className="ml-2">{error}</span>
        </p>
      )}
      <CreateSubContactForm
        validationSchema={schema}
        onSubmit={handleSubmit}
        error={error}
        readonly={readonly}
        initialValues={{ firstName, lastName, dateOfBirth }}
      />
    </Modal>
  );
};

export default PhysicianCreateSubcontactModalModule;
